/**
 * The diff visualization — a wrapping flow of KJV/NASB column pairs.
 * Wrapping preserves the vertical pairing at any width, so long verses
 * flow to more lines instead of scrolling horizontally.
 */

import type { BibleLocation, VariantAnalysis } from '@skjv/common';
import { LoadingSpinner } from '@components/icons';
import { useComparisonData } from './use-comparison-data';
import { groupSlots } from './alignment';
import { WordCellGroup } from './word-cell';

interface ComparisonStripsProps {
  analysis: VariantAnalysis;
  location: BibleLocation;
}

export function ComparisonStrips({ analysis, location }: ComparisonStripsProps) {
  const { slots, glosses, isLoading, hasError } = useComparisonData(analysis, location);

  if (isLoading) {
    return (
      <div className="analysis-diff-loading">
        <LoadingSpinner className="h-6 w-6" />
        <span>Loading comparison…</span>
      </div>
    );
  }

  if (hasError || slots.length === 0) {
    return <p className="analysis-diff-error">Word-level comparison unavailable.</p>;
  }

  return (
    <div>
      <h4 className="verse-analysis-section-title">COMPARE</h4>
      <p className="analysis-diff-legend">
        <span className="analysis-legend-kjv">KJV (top)</span>
        {' · '}
        <span className="analysis-legend-nasb">NASB (bottom, Strong&apos;s gloss)</span>
        {' — '}
        <span className="analysis-legend-removed">missing in NASB</span>
        {', '}
        <span className="analysis-legend-added">added in NASB</span>
      </p>
      <div className="analysis-diff">
        {groupSlots(slots).map((group, i) => (
          <WordCellGroup key={i} slots={group} glosses={glosses} location={location} />
        ))}
      </div>
    </div>
  );
}
